import { UIHeading2 } from '@cian/ui-kit';
import cn from 'classnames';
import * as React from 'react';

import * as styles from './OfferCardHorizontal.css';
import { Labels } from '../Labels';
import { PromotionDescription } from '../PromotionDescription';
import { Address } from './components/Address';
import { Branding } from './components/Branding';
import { Description } from './components/Description';
import { Features } from './components/Features';
import { getFeaturesList } from './components/Features/utils';
import { Price } from './components/Price';
import { TPriceVariant } from './components/Price/utils';
import { Rating } from './components/Rating';
import { Thumbnails } from './components/Thumbnails';
import { UndergroundStations } from './components/UndergroundStations';
import { ETestId } from '../../constants';
import { ButtonsContainer } from '../../containers/Buttons';
import { CarouselContainer } from '../../containers/Carousel';
import { IDailyRentOfferForMobileSite } from '../../repositories/dailyrent/entities/daily_rent_offers_for_web/DailyRentOfferForMobileSite';

interface IOfferCard {
  offer: IDailyRentOfferForMobileSite;
  hasDates: boolean;
  showBrandingBlock: boolean;
  showThumbnails?: boolean;
  showRating?: boolean;
  showButtons?: boolean;
  showPromotionLabels: boolean;
  priceVariant?: TPriceVariant;
  carouselControls?: React.ReactNode;
  offerLink: string;
  isSingle: boolean;
  promotionDescriptionLearnMoreUrl?: string;

  onCardClick(): void;
  onMoreClick(): void;
  onBookClick(): void;
  onCallClick(): void;
  onChatClick(): void;
  onSlideChange(): void;
  onBuildingClick(): void;
  onMouseEnter?(): void;
  onMouseLeave?(): void;
}

export const OfferCardHorizontal = ({
  offer,
  hasDates,
  showBrandingBlock,
  showThumbnails = true,
  showRating = true,
  showButtons = true,
  showPromotionLabels,
  priceVariant = 'default',
  carouselControls,
  isSingle,
  offerLink,
  promotionDescriptionLearnMoreUrl,
  onCardClick,
  onMoreClick,
  onBookClick,
  onCallClick,
  onChatClick,
  onSlideChange,
  onBuildingClick,
  onMouseEnter,
  onMouseLeave,
}: IOfferCard) => {
  const isThumbnailsVisible = showThumbnails && offer.isPromoted && offer.media?.length > 1;
  const carouselRoundClass = isThumbnailsVisible ? styles['carousel-round-top'] : styles['carousel-round-full'];
  const carouselFullHeightClass = isThumbnailsVisible ? undefined : styles['carousel-full-height'];

  // Бэк может не отдать vasTitle (например, у рекомендаций) — тогда заголовком становятся фичи,
  // а их мелкая строка не дублируется.
  const fallbackTitle = getFeaturesList(offer.features).join(', ');
  const titleText = offer.vasTitle || fallbackTitle;
  const hasVasTitle = Boolean(offer.vasTitle);
  const isFeaturesRowVisible = !offer.isHotel && Boolean(offer.vasTitle);

  return (
    <div
      className={cn(styles['root'], {
        [styles['root_reduced-padding']]: isSingle && showBrandingBlock,
        [styles['root_enhanced-padding']]: isSingle && !showBrandingBlock,
      })}
      data-testid={ETestId.OfferCard}
      data-partner={offer.isCianPartner}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {showBrandingBlock && offer.author && (
        <a
          className={styles['branding']}
          href={offerLink}
          target="_blank"
          rel="noreferrer"
          onClick={onCardClick}
          data-testid={ETestId.OfferCardAuthorBranding}
          data-linkname="link_to_card"
        >
          <Branding {...offer.author} />
        </a>
      )}
      <div className={styles['main']}>
        <div className={styles['carousel-wrapper']}>
          <CarouselContainer
            className={cn(styles['carousel'], carouselRoundClass, carouselFullHeightClass)}
            offer={offer}
            offerLink={offerLink}
            onSlideChange={onSlideChange}
            onClick={onCardClick}
            extraControls={
              <div className={styles['modal-controls']}>
                <div className={styles['button-container']}>{carouselControls}</div>
              </div>
            }
          />
          {isThumbnailsVisible && (
            <a
              className={styles['wrapper-link']}
              href={offerLink}
              target="_blank"
              rel="noreferrer"
              data-linkname="link_to_card"
              onClick={onCardClick}
            >
              <div className={styles['thumbnails-container']}>
                <Thumbnails media={offer.media} />
              </div>
            </a>
          )}
        </div>

        <div className={styles['content']}>
          {/* draggable=false: иначе зажатие мышью на лейблах запускает нативный drag ссылки и ломает drag-scroll (useDragScroll в Labels) */}
          <a
            className={styles['content-link']}
            draggable={false}
            href={offerLink}
            target="_blank"
            rel="noreferrer"
            data-linkname="link_to_card"
            onClick={onCardClick}
          >
            <div className={styles['title']}>
              <UIHeading2 color={hasVasTitle ? 'text-main-default' : 'text-primary-default'}>
                {titleText}
                {offer.isHotel && offer.starRating && (
                  <>
                    &nbsp;{offer.starRating}&nbsp;
                    <span className={styles['star']} />
                  </>
                )}
              </UIHeading2>
              {isFeaturesRowVisible && <Features features={offer.features} />}
            </div>

            <Labels labels={offer.photoLabels} className={styles['labels']} />

            <div className={styles['underground']}>
              <UndergroundStations {...offer} />
              <Address address={offer.geo.address} />
            </div>

            <div className={styles['price']}>
              {showPromotionLabels && (
                <PromotionDescription
                  label={offer.promotionDescription}
                  learnMoreUrl={promotionDescriptionLearnMoreUrl}
                />
              )}
              <Price
                price={offer.price}
                hasDates={hasDates}
                variant={priceVariant}
                originalPrice={offer.discount?.oldPrice}
                discountPercent={offer.discount?.percent}
              />
              {showRating && (
                <div className={styles['rating']}>
                  <Rating offer={offer} />
                </div>
              )}
            </div>

            {offer.description && <Description text={offer.description} />}
          </a>
          {showButtons && (
            <div data-testid="OfferCardButtonContainer">
              <ButtonsContainer
                offer={offer}
                offerLink={offerLink}
                onMoreClick={onMoreClick}
                onBookClick={onBookClick}
                onCallClick={onCallClick}
                onChatClick={onChatClick}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
